import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaHeart, FaShoppingCart, FaUser, FaSearch, FaBars, FaTimes, FaChevronDown, FaSignOutAlt, FaBox, FaUserCircle } from 'react-icons/fa';
import { toggleMobileMenu, closeMobileMenu, openSearch } from '../../redux/slices/uiSlice';
import { logout } from '../../redux/slices/authSlice';
import { fetchCart } from '../../redux/slices/cartSlice';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const userDropdownRef = useRef(null);
  const catDropdownRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { totalItems } = useSelector((state) => state.cart);
  const { isMobileMenuOpen } = useSelector((state) => state.ui);
  const { categories } = useSelector((state) => state.products);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) setShowUserDropdown(false);
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target)) setShowCategoryDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(closeMobileMenu());
    setShowUserDropdown(false);
    setShowCategoryDropdown(false);
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart());
    }
  }, [isAuthenticated, dispatch]);

  // Awaited so navigation happens after the server has cleared the session
  // cookie; otherwise a fast reload could race the still-valid cookie.
  const handleLogout = async () => {
    setShowUserDropdown(false);
    await dispatch(logout());
    navigate('/');
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/shop', label: 'Shop' },
    { path: '/category/festival-collection', label: 'Festival Collection' },
    { path: '/category/new-arrivals', label: 'New Arrivals' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const dropdownItemStyle = {
    color: '#333',
    fontSize: '0.88rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    transition: 'background 0.2s',
  };

  const navLinkBase = {
    position: 'relative',
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.92rem',
    paddingBottom: '4px',
    borderBottom: 'none',
    overflow: 'hidden',
  };

  return (
    <>
      <style>{`
        nav.navbar { overflow: visible !important; }
        .nav-link-rrc::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #6B1420, #D4A853, #6B1420);
          transition: width 0.35s cubic-bezier(0.25, 0.8, 0.25, 1), left 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
          border-radius: 2px;
        }
        .nav-link-rrc:hover::after {
          width: 100%;
          left: 0;
        }
        .nav-link-rrc.active-rrc::after {
          width: 100%;
          left: 0;
          background: linear-gradient(90deg, #D4A853, #6B1420, #D4A853);
          animation: shimmer-underline 2s ease-in-out infinite;
        }
        @keyframes shimmer-underline {
          0%, 100% { background-position: 0% 50%; opacity: 1; }
          50% { opacity: 0.7; }
        }
        .nav-link-rrc:hover {
          text-shadow: 0 0 8px rgba(212,168,83,0.3);
        }
        .nav-icon-btn {
          width: 42px; height: 42px;
          display: inline-flex; align-items: center; justify-content: center;
          border-radius: 50%;
          background: transparent;
          border: none;
          color: #fff !important;
          font-size: 1.15rem;
          position: relative;
          cursor: pointer;
          transition: background 0.25s, transform 0.25s;
          text-decoration: none;
        }
        .nav-icon-btn:hover {
          background: rgba(255,255,255,0.15);
          transform: translateY(-1px);
        }
        .nav-icon-btn .icon-badge {
          position: absolute;
          top: -1px;
          right: -3px;
          min-width: 19px;
          height: 19px;
          padding: 0 4px;
          border-radius: 10px;
          background: linear-gradient(135deg, #6B1420, #8a1a2a);
          color: #fff;
          font-size: 0.66rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #1B3A63;
          line-height: 1;
        }
        @media (max-width: 991.98px) {
          .navbar .container { flex-wrap: nowrap !important; min-width: 0; overflow: hidden; }
          .navbar-brand { margin-right: 8px; min-width: 0; overflow: hidden; }
        }
        @media (max-width: 575.98px) {
          .nav-icon-btn { width: 34px; height: 34px; font-size: 0.9rem; }
          .rrc-nav-icons { gap: 4px !important; }
        }
        @media (max-width: 379.98px) {
          .nav-icon-btn { width: 30px; height: 30px; font-size: 0.78rem; gap: 2px; }
          .rrc-nav-icons { gap: 3px !important; }
        }
      `}</style>
      <nav
        className="navbar navbar-expand-lg fixed-top"
        style={{
          background: scrolled
            ? 'linear-gradient(135deg, #1B3A63 0%, #122845 100%)'
            : 'linear-gradient(135deg, #1B3A63 0%, #1B3A63 100%)',
          padding: scrolled ? '8px 0' : '12px 0',
          transition: 'all 0.3s ease',
          boxShadow: scrolled ? '0 4px 20px rgba(27, 58, 99, 0.3)' : 'none',
          zIndex: 1050,
          borderBottom: 'none',
        }}
      >
        <div className="container">
          <Link
            to="/"
            className="navbar-brand d-flex align-items-center"
            style={{ fontFamily: "'Vollkorn', serif", color: '#fff', fontWeight: 700 }}
          >
            <img
              src="/images/logo.png"
              alt="RRC Logo"
              style={{
                width: scrolled ? '32px' : '38px',
                height: scrolled ? '32px' : '38px',
                objectFit: 'contain', borderRadius: '8px',
                marginRight: '10px', background: 'rgba(255,255,255,0.92)',
                padding: '2px', transition: 'all 0.3s ease',
              }}
            />
            <span style={{ fontSize: scrolled ? 'clamp(0.65rem, 2.4vw, 1.15rem)' : 'clamp(0.7rem, 3.2vw, 1.4rem)', lineHeight: 1.1, transition: 'font-size 0.3s ease', letterSpacing: '0.5px', display: 'inline-block', whiteSpace: 'nowrap' }}>
              Radha Rani Collection
            </span>
          </Link>

          <div className="rrc-nav-icons d-flex align-items-center d-lg-none" style={{ gap: '6px' }}>
            <button onClick={() => dispatch(openSearch())} className="nav-icon-btn" title="Search">
              <FaSearch />
            </button>
            <Link to="/wishlist" className="nav-icon-btn" title="Wishlist"><FaHeart /></Link>
            <Link to="/cart" className="nav-icon-btn" title="Cart">
              <FaShoppingCart />
              {totalItems > 0 && (
                <span className="icon-badge">{totalItems > 99 ? '99+' : totalItems}</span>
              )}
            </Link>
            <button onClick={() => dispatch(toggleMobileMenu())} className="nav-icon-btn" title="Menu">
              {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav mx-auto">
              {navLinks.map((link) => (
                <li
                  className="nav-item"
                  key={link.path}
                  ref={link.path === '/shop' ? catDropdownRef : undefined}
                  onMouseEnter={() => link.path === '/shop' && setShowCategoryDropdown(true)}
                  onMouseLeave={() => link.path === '/shop' && setShowCategoryDropdown(false)}
                >
                  {link.path === '/shop' ? (
                    <div className="position-relative">
                      <Link
                        to="/shop"
                        className={`nav-link d-flex align-items-center nav-link-rrc ${isActive(link.path) ? 'active-rrc' : ''}`}
                        style={{ ...navLinkBase, color: isActive(link.path) ? '#fff' : 'rgba(255,255,255,0.85)' }}
                      >
                        {link.label} <FaChevronDown style={{ marginLeft: '6px', fontSize: '0.7rem' }} />
                      </Link>
                      {showCategoryDropdown && categories.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', borderRadius: '8px', boxShadow: '0 8px 30px rgba(27,58,99,0.15)', minWidth: '220px', padding: '8px 0', zIndex: 1000 }}>
                          <Link to="/shop" className="d-block px-3 py-2" style={{ color: '#1B3A63', fontWeight: 600, fontSize: '0.88rem' }}>All Products</Link>
                          {categories.slice(0, 8).map((cat) => (
                            <Link
                              key={cat._id || cat.slug}
                              to={`/category/${cat.slug}`}
                              className="d-block px-3 py-2"
                              style={{ color: '#333', fontSize: '0.85rem' }}
                              onMouseEnter={(e) => { e.target.style.background = '#F4EDE4'; e.target.style.color = '#1B3A63'; }}
                              onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#333'; }}
                            >
                              {cat.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`nav-link nav-link-rrc ${isActive(link.path) ? 'active-rrc' : ''}`}
                      style={{ ...navLinkBase, color: isActive(link.path) ? '#fff' : 'rgba(255,255,255,0.85)' }}
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            <div className="d-none d-lg-flex align-items-center" style={{ gap: '8px', marginLeft: 'auto' }}>
              <button onClick={() => dispatch(openSearch())} className="nav-icon-btn" title="Search">
                <FaSearch />
              </button>
              <Link to="/wishlist" className="nav-icon-btn" title="Wishlist">
                <FaHeart />
              </Link>
              <Link to="/cart" className="nav-icon-btn" title="Cart">
                <FaShoppingCart />
                {totalItems > 0 && (
                  <span className="icon-badge">{totalItems > 99 ? '99+' : totalItems}</span>
                )}
              </Link>
              <div className="position-relative" ref={userDropdownRef}>
                <button onClick={() => setShowUserDropdown(!showUserDropdown)} className="nav-icon-btn" title="Account">
                  <FaUser />
                </button>
                {showUserDropdown && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', borderRadius: '8px', boxShadow: '0 8px 30px rgba(27,58,99,0.15)', minWidth: '200px', padding: '8px 0', zIndex: 1000 }}>
                    {isAuthenticated ? (
                      <>
                        <div className="px-3 py-2" style={{ borderBottom: '1px solid #eee' }}>
                          <p style={{ margin: 0, fontWeight: 600, color: '#1B3A63', fontSize: '0.9rem' }}>{user?.name || 'User'}</p>
                          <p style={{ margin: 0, color: '#888', fontSize: '0.78rem' }}>{user?.email}</p>
                        </div>
                        <Link to="/profile" className="d-block px-3 py-2" style={dropdownItemStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaUserCircle /> My Profile
                        </Link>
                        <Link to="/my-orders" className="d-block px-3 py-2" style={dropdownItemStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaBox /> My Orders
                        </Link>
                        <Link to="/track" className="d-block px-3 py-2" style={dropdownItemStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          🚚 Track Order
                        </Link>
                        {user?.role === 'admin' && (
                          <Link to="/admin" className="d-block px-3 py-2 fw-semibold" style={{ ...dropdownItemStyle, color: '#C9A24B' }} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                            ⚙️ Admin Panel
                          </Link>
                        )}
                        <button onClick={handleLogout} className="d-flex align-items-center w-100 px-3 py-2" style={{ ...dropdownItemStyle, background: 'none', border: 'none', textAlign: 'left' }} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaSignOutAlt /> Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/login" className="d-block px-3 py-2" style={dropdownItemStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaUser /> Login
                        </Link>
                        <Link to="/register" className="d-block px-3 py-2" style={dropdownItemStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#F4EDE4'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <FaUserCircle /> Create Account
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div
          className="d-lg-none"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1040,
            animation: 'fadeIn 0.3s ease',
          }}
          onClick={() => dispatch(closeMobileMenu())}
        />
      )}

      <div
        className="d-lg-none"
        style={{
          position: 'fixed',
          top: 0,
          left: isMobileMenuOpen ? 0 : '-100%',
          width: '280px',
          height: '100vh',
          background: 'linear-gradient(180deg, #1B3A63 0%, #122845 100%)',
          zIndex: 1060,
          transition: 'left 0.3s ease',
          overflowY: 'auto',
          paddingTop: '20px',
        }}
      >
        <div className="d-flex justify-content-end px-3 mb-3">
          <button onClick={() => dispatch(closeMobileMenu())} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.3rem' }}>
            <FaTimes />
          </button>
        </div>
        <div className="px-3 mb-4">
          <h5 style={{ fontFamily: "'Vollkorn', serif", color: '#fff', marginBottom: '4px' }}>Radha Rani Collection</h5>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>Premium Ethnic Wear</p>
        </div>
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className="d-block px-4 py-3"
            style={{ color: isActive(link.path) ? '#fff' : 'rgba(255,255,255,0.8)', fontWeight: isActive(link.path) ? 600 : 400, borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '0.95rem' }}
          >
            {link.label}
          </Link>
        ))}
        {categories.slice(0, 6).map((cat) => (
          <Link
            key={cat._id || cat.slug}
            to={`/category/${cat.slug}`}
            className="d-block px-4 py-2"
            style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
          >
            {cat.name}
          </Link>
        ))}
        <div className="px-4 mt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
          {isAuthenticated ? (
            <>
              <p style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{user?.name}</p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{user?.email}</p>
              <Link to="/profile" className="d-block py-2" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>My Profile</Link>
              <Link to="/my-orders" className="d-block py-2" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>My Orders</Link>
              <Link to="/track" className="d-block py-2" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>Track Order</Link>
              {user?.role === 'admin' && (
                <Link to="/admin" className="d-block py-2 fw-semibold" style={{ color: '#C9A24B', fontSize: '0.9rem' }}>⚙️ Admin Panel</Link>
              )}
              <button onClick={handleLogout} className="d-block py-2" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', textAlign: 'left' }}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="d-block py-2" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>Login</Link>
              <Link to="/register" className="d-block py-2" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>Create Account</Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;
